import { AxiosResponse } from 'axios';
import { isArray, isNumber } from 'lodash-es';

import { APP_FORM_CONFIG } from '@shared/config/task';
import { tryParseJson } from '@shared/utils/json';
import { isProd } from '@shared/env/';

import { APPS_ENDPOINTS } from '../filter/publications';
import { COMMON_POSTS_ENDPOINTS, IMAGES_ENDPOINTS } from '../filter/images';
import { DETAIL_ENDPOINTS } from '../filter/detail';

type TAppsType = 'common' | 'home' | 'favorite' | 'collection' | 'collection-detail';
type TImagesType = 'common' | 'favorite' | 'task';

const isApps = (data: any, type: TAppsType) => {
	const list = data?.list ?? [];
	if (!list.length) return false;

	const first = list[0];
	let resource = null;

	switch (type) {
		case 'home':
			resource = first;
			break;
		case 'favorite':
			resource = first.resource?.resource;
			break;
		case 'collection-detail':
			resource = first.pubApp?.resource;
			break;
		case 'collection':
			return isArray(first.pubRespList);
		default:
			resource = first.resource;
			break;
	}

	return Boolean(resource && 'id' in resource);
};

const isImage = (data: any, type: TImagesType) => {
	const list = data?.list ?? [];
	if (!list.length) return false;

	const first = list[0];
	let resource = null;

	switch (type) {
		case 'favorite':
			resource = first.resource;
			break;
		case 'task':
			resource = first.images?.[0];
			break;
		default:
			resource = first;
			break;
	}

	return Boolean('appInfo' in first && 'param' in resource);
};

const isPosts = (data: any) => {
	const list = data?.list ?? [];
	if (!list.length) return false;

	const first = list[0];

	return Boolean('resources' in first);
};

const isSearch = (data: any) => {
	if (!data) return false;
	return 'appInfo' in data && 'imageInfo' in data;
};

const makeErrorRes = (res: AxiosResponse<any, any>) => {
	res.data.data = null;
	res.data.code = -1;
	res.data.msg = 'Please refresh your browser to get the latest version';
};

export const isAllowVersion = (resource: any) => {
	if (!resource) return true;
	// 表单式应用无需处理
	if (resource.type === 'node') return true;

	// 目前两种判断版本的方式
	// 1. resource.formVersion
	// 2. resource.form 中找到 meta 标签查看 meta.version
	let version = 1;
	if (isNumber(resource.formVersion)) {
		version = resource.formVersion;
	} else if (resource.form) {
		const arr = tryParseJson(resource.form);
		if (isArray(arr)) {
			const meta = arr.find((item) => item?.type === 'meta');
			isNumber(meta?.version) && (version = meta.version);
		}
	}

	return APP_FORM_CONFIG.ALLOW_VERSIONS.includes(version);
};

const filterApps = <T extends Record<string, any>>(list: T[], type: TAppsType) => {
	return list.filter((app) => {
		let resource: any = null;

		switch (type) {
			case 'home':
				resource = app;
				break;
			case 'favorite':
				resource = app.resource?.resource;
				break;
			case 'collection-detail':
				resource = app.pubApp?.resource;
				break;
			default:
				resource = app.resource;
				break;
		}

		return isAllowVersion(resource);
	});
};

const filterImages = <T extends Record<string, any>>(images: T[], type: TImagesType) => {
	return images.filter((image) => {
		let form = '';

		switch (type) {
			case 'task':
				form = image.images?.[0]?.appInfo?.form;
				break;
			case 'favorite':
				form = image.resource?.appInfo?.form;
				break;
			default:
				form = image.appInfo?.form;
				break;
		}

		return isAllowVersion({ form });
	});
};

const filterPosts = <T extends Record<string, any>>(posts: T[]) => {
	return posts.filter((post) => {
		const form = post.resources?.[0]?.resource?.appInfo?.form;
		return isAllowVersion({ form });
	});
};

const filterDataByFormVersionInner = (res: AxiosResponse<any, any>) => {
	const url = res.config.url;
	const data = res.data?.data;

	if (!data || !url) return;

	// - 搜索页数据过滤
	if (APPS_ENDPOINTS.SEARCH.some((reg) => reg.test(url))) {
		if (!isSearch(data)) return;

		const apps = data.appInfo?.list;
		const recommendApps = data.appRecommendInfo?.list;
		const images = data.imageInfo?.list;
		const recommendImages = data.imageRecommendInfo?.list;

		if (apps?.length) {
			data.appInfo.list = filterApps(apps, 'common');
		}

		if (recommendApps?.length) {
			data.appRecommendInfo.list = filterApps(recommendApps, 'common');
		}

		if (images?.length) {
			data.imageInfo.list = filterImages(images, 'common');
		}

		if (recommendImages?.length) {
			data.imageRecommendInfo.list = filterImages(recommendImages, 'common');
		}

		return;
	}

	// 除了搜索其他都是 get
	if (res.config.method !== 'get') return;

	// - 详情页手动报错
	if (DETAIL_ENDPOINTS.APP.test(url)) {
		const resource = data.resource;
		if (!isAllowVersion(resource)) {
			makeErrorRes(res);
			return;
		}
	} else if (DETAIL_ENDPOINTS.IMAGE.test(url)) {
		const appInfo = data.appInfo;
		if (!isAllowVersion(appInfo)) {
			makeErrorRes(res);
			return;
		}
	}

	// --- 应用列表过滤
	if (APPS_ENDPOINTS.COMMON.some((reg) => reg.test(url))) {
		if (!isApps(data, 'common')) return;
		data.list = filterApps(data.list, 'common');
		return;
	} else if (APPS_ENDPOINTS.HOME.some((reg) => reg.test(url))) {
		if (!isApps(data, 'home')) return;
		data.list = filterApps(data.list, 'home');
		return;
	} else if (APPS_ENDPOINTS.HOME_FAVORITE.some((reg) => reg.test(url))) {
		if (!isApps(data, 'favorite')) return;
		data.list = filterApps(data.list, 'favorite');
		return;
	} else if (APPS_ENDPOINTS.COLLECTION.some((reg) => reg.test(url))) {
		if (!isApps(data, 'collection')) return;
		data.list = data.list.map((collection: any) => {
			collection.pubRespList = filterApps(collection.pubRespList, 'collection');
			return collection;
		});
		return;
	} else if (APPS_ENDPOINTS.COLLECTION_DETAIL.some((reg) => reg.test(url))) {
		if (!isApps(data, 'collection-detail')) return;
		data.list = filterApps(data.list, 'collection-detail');
		return;
	}

	// --- 图片列表过滤
	if (IMAGES_ENDPOINTS.COMMON.some((reg) => reg.test(url))) {
		if (!isImage(data, 'common')) return;
		data.list = filterImages(data.list, 'common');
		return;
	} else if (IMAGES_ENDPOINTS.FAVORITE.some((reg) => reg.test(url))) {
		if (!isImage(data, 'favorite')) return;
		data.list = filterImages(data.list, 'favorite');
		return;
	} else if (IMAGES_ENDPOINTS.TASK.some((reg) => reg.test(url))) {
		if (!isImage(data, 'task')) return;
		data.list = filterImages(data.list, 'task');
		return;
	}

	// --- post 过滤
	if (COMMON_POSTS_ENDPOINTS.some((reg) => reg.test(url))) {
		if (!isPosts(data)) return;
		data.list = filterPosts(data.list);
		return;
	}
};

/**
 * 过滤当前不支持的数据
 * @param res
 */
export const filterDataByFormVersion = (res: AxiosResponse<any, any>) => {
	try {
		filterDataByFormVersionInner(res);
	} catch (error) {
		!isProd && console.error('数据过滤失败', error);
	}
};
